import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import toast from "react-hot-toast";
import ReactHotToaster from "../../components/common/ReactHotToaster";
import { axiosConfig } from "../../axios/axiosConfig";

const LoginUsersHistory = () => {
  const today = new Date().toISOString().split("T")[0];
  const firstOfYear = `${new Date().getFullYear()}-01-01`;

  const [startDate, setStartDate] = useState(firstOfYear);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end date.");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: `/user/export-login-count-history`,
        params: { start_date: startDate, end_date: endDate },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type:
          res.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `login-history-${startDate}-to-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Downloaded successfully!");
    } catch (error) {
      toast.error(
        error?.response?.data?.response || error?.message || "Download failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "start",
              gap: "10px",
            }}
          >
            <HistoryIcon sx={{ color: "#fff", fontSize: 32 }} />
            <Box>
              <Typography
                sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}
              >
                Login Users History
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}
              >
                Export login count history as Excel
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 180,

                // Input text color
                "& .MuiInputBase-input": {
                  color: "#ffffff",
                },

                // Label color
                "& .MuiInputLabel-root": {
                  color: "#ffffff",
                },

                // Border color
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ffffff",
                  },
                  "&:hover fieldset": {
                    borderColor: "#ffffff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ffffff",
                  },
                },

                // Calendar icon color
                "& .MuiSvgIcon-root": {
                  color: "#ffffff",
                },
                "& input::-webkit-calendar-picker-indicator": {
                  filter: "invert(1)",
                  cursor: "pointer",
                },
              }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 180,

                // Input text color
                "& .MuiInputBase-input": {
                  color: "#ffffff",
                },

                // Label color
                "& .MuiInputLabel-root": {
                  color: "#ffffff",
                },

                // Border color
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ffffff",
                  },
                  "&:hover fieldset": {
                    borderColor: "#ffffff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ffffff",
                  },
                },

                // Calendar icon color
                "& .MuiSvgIcon-root": {
                  color: "#ffffff",
                },
                "& input::-webkit-calendar-picker-indicator": {
                  filter: "invert(1)",
                  cursor: "pointer",
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={loading}
              onClick={handleDownload}
              sx={{
                background: "linear-gradient(135deg, #016551, #014d3d)",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                px: 3,
                boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                  boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
                },
                "&:disabled": { opacity: 0.7 },
              }}
            >
              {loading ? "Downloading..." : "Download Excel"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filter + Download Card */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Card Section Header */}
        <Box sx={{ background: "#04343a", px: "20px", py: "12px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
            Select Date Range
          </Typography>
        </Box>
      </Box>

      <ReactHotToaster />
    </Box>
  );
};

export default LoginUsersHistory;
